"use client";

import { useLayoutEffect, useState } from "react";
import { debounce } from "../../delay";
import {
  MAX_TOP_SCROLLPOS,
  MAX_TOP_SCROLLPOS_MOBILE,
  ELEMENTS,
  OFFSET_BOTTOM
} from "./constants";
import type { TElementsType } from "./types";

export const useProductTableScroll = (isMobile: boolean) => {
  const [showFixedHeader, setShowFixedHeader] = useState(false);
  const [elements, setElements] = useState<TElementsType>(null);

  const MAX_TOP = isMobile ? MAX_TOP_SCROLLPOS_MOBILE : MAX_TOP_SCROLLPOS;
  const DELAY = 20;
  const scrollBarWidth = 0;

  // set html elements
  useLayoutEffect(() => {
    const el0: HTMLElement | null = document.getElementById(ELEMENTS.a1);
    const el1: HTMLElement | null = document.getElementById(ELEMENTS.a2);
    const el2: HTMLElement | null = document.getElementById(ELEMENTS.b);
    const el3: HTMLElement | null = document.getElementById(ELEMENTS.c);
    if (!el0 || !el1 || !el2 || !el3) return;

    setElements({
      productListTableWrapper: el0,
      productListTableHeadWrapper: el1,
      scrollableTableHeader: el2,
      fixedTableHeader: el3
    });
  }, [isMobile]);

  // set width of #productListTableWrapper to #productListTableHeadWrapper
  useLayoutEffect(() => {
    if (!elements) return;

    // Debounce to avoid the function fire multiple times
    const handleTableSizeDebounced = debounce(() => {
      const sourceWidth = window.getComputedStyle(
        elements.productListTableWrapper
      ).width;
      const sourceWidthNum = parseInt(sourceWidth) - scrollBarWidth;

      elements.productListTableHeadWrapper.style.width = `${sourceWidthNum}px`;

      // move the horizontal scrollbar a bit to trigger resizing cloned table header
      elements.productListTableWrapper.scrollLeft += 1;
    }, DELAY);

    handleTableSizeDebounced();

    window.addEventListener("resize", handleTableSizeDebounced);

    return () => {
      window.removeEventListener("resize", handleTableSizeDebounced);
    };
  }, [DELAY, elements]);

  // update "showFixedHeader" depending on table scroll position
  useLayoutEffect(() => {
    if (!elements) return;

    const handleScroll = () => {
      const el: HTMLElement | null = document.getElementById(ELEMENTS.a0);
      if (!el) {
        setShowFixedHeader(false);
        return;
      }

      const { topIsVisible, bottomIsVisible } = isScrollTableVisibleOnScreen(
        el,
        MAX_TOP
      );

      let newShowFixedHeader = false;
      if (topIsVisible) newShowFixedHeader = false;
      if (!topIsVisible) newShowFixedHeader = true;
      // hide if bottom of table is out of view
      if (!bottomIsVisible) newShowFixedHeader = false;

      setShowFixedHeader(newShowFixedHeader);
    };

    // Debounce to avoid the function fire multiple times
    const handleResizeDebounced = debounce(() => handleScroll(), DELAY);

    document.addEventListener("scroll", handleResizeDebounced);

    return () => {
      document.removeEventListener("scroll", handleResizeDebounced);
      setShowFixedHeader(false);
    };
  }, [DELAY, MAX_TOP, elements, isMobile]);

  // show/hide fixed table header
  useLayoutEffect(() => {
    if (!elements) return;

    if (!showFixedHeader) {
      elements.scrollableTableHeader.style.visibility = "visible";
      elements.productListTableHeadWrapper.className =
        "productListTableHeadWrapper hide";
    }
    if (showFixedHeader) {
      elements.scrollableTableHeader.style.visibility = "collapse";
      elements.productListTableHeadWrapper.className =
        "productListTableHeadWrapper show";

      cloneTableHead(elements);
    }
  }, [elements, showFixedHeader]);

  // clone table vertical scrolling
  useLayoutEffect(() => {
    if (!showFixedHeader || !elements) return;

    const cloneScrollPosDebounced = debounce(() => {
      // set left scroll postion of target element to position of source element
      elements.productListTableHeadWrapper.scrollLeft =
        elements.productListTableWrapper.scrollLeft;
    }, 1);

    elements?.productListTableWrapper.addEventListener(
      "scroll",
      cloneScrollPosDebounced
    );

    return () => {
      elements?.productListTableWrapper.removeEventListener(
        "scroll",
        cloneScrollPosDebounced
      );
    };
  }, [elements, showFixedHeader]);
};

const cloneTableHead = (elements: TElementsType) => {
  if (!elements) return;

  // clone the table header content
  const source = elements.scrollableTableHeader.innerHTML;
  if (!source) return;
  elements.fixedTableHeader.innerHTML = source;

  // set the width of each table header cell to a fixed value
  const thead = elements.scrollableTableHeader;
  const theadClone = elements.fixedTableHeader;

  const theadCols = thead.querySelectorAll("th");
  const theadCloneCols = theadClone.querySelectorAll("th");

  theadCols.forEach((_, index) => {
    theadCloneCols[index].style.width = `${theadCols[index].offsetWidth}px`;
  });

  // move the horizontal scrollbar a bit to trigger resizing cloned table header
  elements.productListTableWrapper.scrollLeft += 1;

  // const cells = elements.fixedTableHeader.querySelectorAll("th");
  // cells.forEach((cell) => {
  //   const sourceWidth = window.getComputedStyle(cell).getPropertyValue("width");
  //   const roundedWidth = Math.ceil(Number(sourceWidth));

  //   cell.style.width = roundedWidth.toString();
  //   cell.style.minWidth = roundedWidth.toString();
  //   cell.style.maxWidth = roundedWidth.toString();
  // });
};

const isScrollTableVisibleOnScreen = (
  el: HTMLElement,
  MAX_TOP: 128 | 105
): {
  topIsVisible: boolean;
  bottomIsVisible: boolean;
} => {
  if (!el)
    return {
      topIsVisible: false,
      bottomIsVisible: false
    };

  const top = el.getBoundingClientRect().top;
  const topIsVisible = top <= MAX_TOP ? false : true;

  const bottom = el.getBoundingClientRect().bottom;
  const bottomIsVisible = bottom - OFFSET_BOTTOM >= MAX_TOP;

  return {
    topIsVisible,
    bottomIsVisible
  };
};
